import { useInfiniteQuery } from '@tanstack/react-query';
import axios, { isAxiosError } from 'axios';
import { useTranslation } from 'next-i18next';

import { alertDialog } from '@/ui_v3';

export interface PexelsImage {
  id: number;
  src: {
    medium: string;
  };
  alt: string;
  photographer: string;
  url: string;
}

export interface PexelsResponse {
  next_page?: string;
  page?: number;
  photos: PexelsImage[];
  total_results: number;
}

const COUNTRY_CODE_TO_PEXELS_LOCALE: Record<string, string> = {
  KR: 'ko-KR',
  US: 'en-US',
  JP: 'ja-JP',
  ID: 'id-ID',
  TH: 'th-TH',
  PH: 'fil-PH',
  VN: 'vi-VN',
  MY: 'ms-MY',
  UZ: 'uz-UZ',
  FR: 'fr-FR',
  KH: 'km-KH',
  RU: 'ru-RU',
  CN: 'zh-CN',
  TW: 'zh-TW',
  ES: 'es-ES',
  PT: 'pt-PT',
  DE: 'de-DE',
  IT: 'it-IT',
  TR: 'tr-TR',
};

const IMAGES_PER_PAGE = 50;

const getPexelsLocale = (countryCode: string): string => {
  return COUNTRY_CODE_TO_PEXELS_LOCALE[countryCode?.toUpperCase()] || 'en-US';
};

export const useImageSearch = (searchQuery: string, countryCode: string) => {
  const locale = getPexelsLocale(countryCode);
  const { t } = useTranslation(['build', 'common']);

  const fetchPexelsImages = async ({
    queryKey,
    pageParam,
  }: {
    queryKey: string[];
    pageParam: number;
  }): Promise<PexelsResponse | undefined> => {
    const [, searchQuery, locale] = queryKey;
    const initialUrl = `/api/image-search/pexels?query=${searchQuery}&locale=${locale}&per_page=${IMAGES_PER_PAGE}`;

    try {
      const response = await axios.get(`${initialUrl}&page=${pageParam}`);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const status = error.response?.status;
        const isNetworkError = error.message === 'Network Error';
        const isServerError = status && status >= 500;

        if (isNetworkError || isServerError) {
          alertDialog.open({
            header: {
              title: t('build:image.searchTab.failTitle'),
            },
            body: t('build:image.searchTab.failDescription'),
            confirmText: t('common:common.alertDialog.confirmText'),
          });
        }
      }
    }
  };

  return useInfiniteQuery({
    queryKey: ['pexels-images', searchQuery, locale],
    queryFn: ({ queryKey, pageParam }) =>
      fetchPexelsImages({ queryKey, pageParam }),
    getNextPageParam: (lastPage, _, lastPageParam) =>
      lastPage?.next_page ? lastPageParam + 1 : undefined,
    initialPageParam: 1,
    enabled: !!searchQuery.trim(),
  });
};
